/**
 * MerchantTraffic — "Tu tráfico", built to the approved handoff
 * (`Tu tráfico.dc.html`: compact base 2b + map/roll-up 3a/3b + share affordance 4a/4b).
 *
 * Which campaigns bring traffic that's actually worth something — do they engage, do
 * they buy — so whoever holds the invoice can decide where the money goes. We never
 * recommend a budget: the ad platforms stop measuring at the click, we measure after
 * it, and the merchant already knows what they pay. See ACQUISITION-VALUE-SPEC.md.
 *
 * Two things drive most of the decisions here:
 * - **The same screen serves both audiences.** In plenty of stores an agency runs the
 *   campaigns, so the merchant can't act on a finding — only ask. Every line is an
 *   observation that survives being screenshotted into a WhatsApp thread, never an
 *   instruction they can't execute.
 * - **Silence is an output.** Merchants move budget almost daily; the price of a visit
 *   doesn't move that fast. "Igual que la semana pasada" is a real answer that stops a
 *   bad decision, so the resting state is quiet and only a real change gets a chip.
 *
 * No statistics run in this file: placement and stability arrive decided from the
 * reader (compareArms), and the client owns wording only.
 */

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Text, Tag, Button } from '@nimbus-ds/components'
import { useAuth } from '@/hooks/useAuth'
import { authService } from '@/api/auth'
import {
  trafficService,
  tagKey,
  type CampaignTags,
  type Objective,
  type TrafficCampaignRow,
  type TrafficChannelGroup,
  type TrafficFinding,
  type TrafficValueResponse,
} from '@/api/traffic'
import { CampaignMap, mapRows, isMapMeaningful } from '@/components/merchant/CampaignMap'
import { ShareFindingSheet } from '@/components/merchant/ShareFindingSheet'
import { CopyCampaign } from '@/components/merchant/CopyCampaign'
import {
  QUADRANT,
  campaignMetricLine,
  campaignDisplayName,
  isOpaqueCampaignId,
} from '@/components/merchant/quadrants'
import { trackAppEvent } from '@/lib/appTelemetry'
import { fmtInt, fmtMoney } from '@/lib/formatEsAr'

const WINDOW_DAYS = 7
const PERIOD_LABEL = `últimos ${WINDOW_DAYS} días`

/** Measured rows shown per channel before the "ver las N restantes" fold. */
const VISIBLE_ROWS = 4

// ---------------------------------------------------------------------------
// Finding copy — the server decides WHAT is worth saying, this decides how it reads
// ---------------------------------------------------------------------------

type Tone = 'good' | 'attention' | 'neutral'

interface FindingCopy {
  emoji: string
  tone: Tone
  title: string
  body?: string
  /** Whether the card offers "Para tu agencia →". A finding nobody would forward doesn't. */
  shareable: boolean
  /** Only the ambiguous quadrant asks what the campaign was for. */
  nudge: boolean
}

function findingCopy(f: TrafficFinding): FindingCopy | null {
  // «120249001851740553» reads as a campaign someone named that. `campaignDisplayName`
  // gives an ID the noun it needs ("la campaña 1202…") so the sentence stays honest.
  // Every title below opens with it, hence the capital — «Nombre» is unaffected.
  const raw = f.campaign ? campaignDisplayName(f.campaign) : ''
  const name = raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : ''
  switch (f.kind) {
    case 'valioso':
      return {
        emoji: '🟢',
        tone: 'good',
        title: `${name} es la que mejor te viene funcionando.`,
        body: 'Mira tus productos y compra. Es la mejor candidata si vas a poner más plata.',
        shareable: true,
        nudge: false,
      }
    case 'no_sirve': {
      // The complement of the engagement rate, said the way the row says it. The old tail
      // ("revisala con quien maneja tus anuncios") is gone: the "Para tu agencia →"
      // affordance one line below IS that sentence, and saying both is why two of these
      // cards read as a wall.
      const left = f.row?.engagement_rate != null ? Math.round((1 - f.row.engagement_rate) * 100) : null
      return {
        emoji: '🔴',
        tone: 'attention',
        title: `${name} trae gente que entra y se va.`,
        body: left != null ? `De cada 100 visitas, ${left} se van sin mirar nada.` : undefined,
        shareable: true,
        nudge: false,
      }
    }
    case 'interesa_no_cierra':
      return {
        emoji: '🟡',
        tone: 'attention',
        title: `${name} trae gente interesada que no compra.`,
        body: 'Puede ser algo de tu tienda — o que la campaña busque que te conozcan, no que te compren.',
        shareable: true,
        nudge: true,
      }
    case 'interesa_ventas':
      return {
        emoji: '🟡',
        tone: 'attention',
        title: `${name} busca ventas, pero la gente no está comprando.`,
        body: 'Llegan interesados y miran, así que el problema está más cerca de tu tienda que de la campaña.',
        shareable: true,
        nudge: false,
      }
    case 'reconocimiento_ok':
      // Nothing to forward: it's doing its job. No affordance, no alarm hue.
      return {
        emoji: '✅',
        tone: 'neutral',
        title: `${name} está haciendo lo que le pediste: que te conozcan.`,
        shareable: false,
        nudge: false,
      }
    case 'desalineada':
      return {
        emoji: '⚠️',
        tone: 'attention',
        title: `${name} la marcaste para que te conozcan, pero trae gente que ya viene decidida a comprar.`,
        body: 'Puede estar mostrándose a gente que ya te conoce.',
        shareable: true,
        nudge: false,
      }
    case 'sin_utm':
      return {
        emoji: 'ℹ️',
        tone: 'neutral',
        title: `No podemos separar tus campañas de ${f.channel}.`,
        body: 'Las visitas llegan sin identificación de campaña. Si manejás los anuncios vos, o tu agencia, pedí que agreguen etiquetas UTM a los enlaces.',
        shareable: false,
        nudge: false,
      }
    default:
      return null
  }
}

const TONE_CLASS: Record<Tone, string> = {
  good: 'bg-[#e9f6ef] border-[color-mix(in_srgb,#00935b_25%,#e9f6ef)]',
  attention: 'bg-[#fdf4e3] border-[color-mix(in_srgb,#bb5d08_25%,#fdf4e3)]',
  neutral: 'bg-[#f6f6f6] border-[#e7e7e7]',
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

/**
 * The objective question. Two taps, inline, skippable — never a modal and never a
 * settings screen, because the moment it becomes a form merchants stop answering and
 * the correction it feeds rots. "No sé" is stored, so we stop asking.
 */
function NudgeBox({
  onAnswer,
  answered,
  tinted,
}: {
  onAnswer: (o: Objective) => void
  answered: boolean
  tinted?: boolean
}) {
  if (answered) {
    return (
      <div className="rounded-lg border border-[#e7e7e7] bg-[#fafafa] px-3 py-2.5">
        <span className="text-[.8125rem] text-[#5d5d5d]">Listo, lo tenemos en cuenta.</span>
      </div>
    )
  }
  return (
    <div
      className={`flex flex-col gap-2 rounded-lg border px-3 py-2.5 ${
        tinted
          ? 'border-[color-mix(in_srgb,#bb5d08_18%,#fdf4e3)] bg-white/55'
          : 'border-[#e7e7e7] bg-[#fafafa]'
      }`}
    >
      <span className="text-[.8125rem] text-[#0a0a0a]">¿Para qué hiciste esta campaña?</span>
      <div className="flex flex-wrap items-center gap-2">
        <Button appearance="neutral" onClick={() => onAnswer('ventas')}>
          Para vender
        </Button>
        <Button appearance="neutral" onClick={() => onAnswer('reconocimiento')}>
          Para que me conozcan
        </Button>
        <button
          type="button"
          onClick={() => onAnswer('unknown')}
          className="text-xs text-[#888] underline"
        >
          No sé
        </button>
      </div>
    </div>
  )
}

function FindingCard({
  finding,
  copy,
  tags,
  onAnswer,
  onShare,
}: {
  finding: TrafficFinding
  copy: FindingCopy
  tags: CampaignTags
  onAnswer: (row: TrafficCampaignRow, o: Objective) => void
  onShare: (row: TrafficCampaignRow) => void
}) {
  const row = finding.row
  const answered = !!(row && tags[tagKey(row.channel, row.campaign)])
  return (
    <div className={`flex flex-col gap-2.5 rounded-lg border px-4 py-3.5 ${TONE_CLASS[copy.tone]}`}>
      <div className="flex items-start gap-3">
        <span className="text-2xl leading-none">{copy.emoji}</span>
        <div className="flex min-w-0 flex-col gap-1">
          <Text fontWeight="medium">{copy.title}</Text>
          {copy.body && <Text>{copy.body}</Text>}
        </div>
      </div>
      {copy.nudge && row && (
        <NudgeBox tinted answered={answered} onAnswer={(o) => onAnswer(row, o)} />
      )}
      {copy.shareable && row && (
        <div className="flex justify-end pr-0.5">
          <button type="button" onClick={() => onShare(row)} className="text-xs text-[#0059d5]">
            Para tu agencia →
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * The rest row's identity line. Same split the chart tooltip shows (`RestBreakdown`) —
 * the two surfaces must never describe the same bag differently.
 */
function restComposition(row: TrafficCampaignRow): string {
  const untagged = row.rest_untagged_sessions
  const folded = row.rest_folded_sessions
  const parts: string[] = []
  if (untagged) parts.push(`${fmtInt(untagged)} sin campaña identificada`)
  if (folded) parts.push(`${fmtInt(folded)} de campañas más chicas`)
  return parts.length ? parts.join(' · ') : 'visitas que no podemos separar por campaña'
}

function CampaignRow({
  row,
  last,
  showNudge,
  answered,
  onAnswer,
}: {
  row: TrafficCampaignRow
  last: boolean
  showNudge: boolean
  answered: boolean
  onAnswer: (o: Objective) => void
}) {
  const meta = QUADRANT[row.quadrant]
  // Half-placed: we know how they behave, not yet whether they buy. Said, not guessed.
  // "…todavía no si compran" is about the CONVERSION count being unknown. It must not key
  // off `value_per_visit`, which is separately null while revenue coverage ramps — that
  // put the line under rows whose own metric sentence already said "y 2 compran".
  const halfPlaced = row.engagement_rate != null && row.conversion_rate == null
  return (
    <div
      className={`flex items-start gap-2.5 py-3 ${last ? '' : 'border-b border-[#f0f0f0]'} ${
        row.quadrant === 'sin_datos' ? 'opacity-85' : ''
      }`}
    >
      <span className="text-lg leading-snug">{meta.emoji}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <Text fontWeight="medium">{meta.label}</Text>
        {/* An opaque platform ID is shown COMPLETE and in mono — it is the string they'd
            paste into their ads manager to find out which campaign this is. Labelled, so
            it reads as an identifier rather than as a campaign that was named "120249…". */}
        {/* Copy sits on every REAL campaign, ID or name: an 18-digit identifier is hopeless to
            retype, and a machine-generated name ("[ADV] Ventas - Remarketing - …") is barely
            better — both are only useful if they can land in the ads manager's search box. The
            rest row is excluded because there is nothing on the other side to look up. */}
        {row.is_rest ? (
          <span className="truncate text-xs text-[#888]">
            {row.campaign} · {restComposition(row)}
          </span>
        ) : (
          <span className="flex min-w-0 items-center gap-1 text-xs text-[#888]">
            {isOpaqueCampaignId(row.campaign) ? (
              <>
                Campaña <span className="truncate font-mono tabular-nums">{row.campaign}</span>
              </>
            ) : (
              <span className="truncate">{row.campaign}</span>
            )}
            <CopyCampaign value={row.campaign} />
          </span>
        )}
        <span className="text-[.8125rem] leading-relaxed tabular-nums text-[#0a0a0a]">
          {campaignMetricLine(row)}
        </span>
        {halfPlaced && (
          <span className="text-xs text-[#888]">Sabemos cómo se comportan, todavía no si compran.</span>
        )}
        {/* Where the campaign lands people. The same data as the chart tooltip, on the surface
            where it can actually be acted on — this row already carries the copy affordance,
            so the person reading it is one paste away from the ads manager. */}
        {row.landings && row.landings.length > 0 && (
          <div className="mt-0.5 flex flex-col gap-0.5">
            {row.landings.map((l) => (
              <span key={l.path} className="flex items-start gap-1.5 text-xs text-[#888]">
                <span className="shrink-0 tabular-nums">
                  {l.share != null ? `${Math.round(l.share * 100)}%` : '—'}
                </span>
                <span className="break-all text-[#5d5d5d]">{l.path}</span>
              </span>
            ))}
          </div>
        )}
        {/* Only a real move gets a chip; an unchanged row stays quiet on purpose. */}
        {row.stability === 'cambio' && (
          <div className="mt-0.5 flex items-center gap-1.5">
            <Tag appearance="neutral">Cambió esta semana</Tag>
          </div>
        )}
        {showNudge && <div className="mt-1"><NudgeBox answered={answered} onAnswer={onAnswer} /></div>}
      </div>
    </div>
  )
}

function ChannelSection({
  group,
  tags,
  nudgedElsewhere,
  onAnswer,
}: {
  group: TrafficChannelGroup
  tags: CampaignTags
  nudgedElsewhere: Set<string>
  onAnswer: (row: TrafficCampaignRow, o: Objective) => void
}) {
  const [expandedRest, setExpandedRest] = useState(false)
  const [expandedThin, setExpandedThin] = useState(false)

  const measured = group.campaigns.filter((c) => c.quadrant !== 'sin_datos')
  const thin = group.campaigns.filter((c) => c.quadrant === 'sin_datos')
  const shown = expandedRest ? measured : measured.slice(0, VISIBLE_ROWS)
  const rest = measured.length - shown.length
  const dominant = group.dominant_quadrant ? QUADRANT[group.dominant_quadrant] : null
  // Explain the numbers once per channel, and only when they dominate — a single stray
  // ID among named campaigns isn't worth a paragraph.
  const named = measured.filter((c) => !c.is_rest)
  const idHeavy =
    named.length > 0 && named.filter((c) => isOpaqueCampaignId(c.campaign)).length >= named.length / 2

  const headerLine = [
    dominant ? `En general: ${dominant.rollup}` : null,
    `${fmtInt(group.sessions)} visitas`,
    group.campaigns_total > 0
      ? `${fmtInt(group.campaigns_total)} ${group.campaigns_total === 1 ? 'campaña' : 'campañas'}`
      : null,
    group.value_per_visit != null ? `${fmtMoney(group.value_per_visit)} por visita en promedio` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-start gap-2 px-0.5">
        {dominant && <span className="text-lg leading-snug">{dominant.emoji}</span>}
        <div className="flex min-w-0 flex-col gap-0.5">
          <Text fontWeight="medium">{group.channel}</Text>
          <span className="text-xs tabular-nums text-[#5d5d5d]">{headerLine}</span>
          {/* A footnote, sized like one. Same species as `sin_utm` — a tagging-hygiene
              problem the merchant can't see but can forward — but it doesn't earn a boxed
              paragraph above the data it annotates. */}
          {idHeavy && (
            <span className="text-xs leading-relaxed text-[#888]">
              Llegan con número en vez de nombre — buscalo en tu administrador de anuncios, o
              pedí que los enlaces usen el nombre de la campaña.
            </span>
          )}
        </div>
      </div>

      {group.sin_utm ? (
        <div className="rounded-lg border border-[#e7e7e7] bg-[#fafafa] px-4 py-3">
          <span className="text-[.8125rem] leading-relaxed text-[#5d5d5d]">
            Las visitas llegan sin identificación de campaña. Si manejás los anuncios vos, o tu
            agencia, pedí que agreguen etiquetas UTM a los enlaces.
          </span>
        </div>
      ) : (
        <div className="rounded-lg border border-[#e7e7e7] bg-white px-4 py-0.5">
          {shown.map((row, i) => {
            const key = tagKey(row.channel, row.campaign)
            return (
              <CampaignRow
                key={key}
                row={row}
                last={i === shown.length - 1 && rest === 0 && thin.length === 0}
                showNudge={
                  row.quadrant === 'interesa_no_cierra' && !row.is_rest && !nudgedElsewhere.has(key)
                }
                answered={!!tags[key]}
                onAnswer={(o) => onAnswer(row, o)}
              />
            )
          })}

          {thin.length > 0 &&
            (expandedThin ? (
              thin.map((row, i) => (
                <CampaignRow
                  key={tagKey(row.channel, row.campaign)}
                  row={row}
                  last={i === thin.length - 1 && rest === 0}
                  showNudge={false}
                  answered={false}
                  onAnswer={() => {}}
                />
              ))
            ) : (
              <div
                className={`flex items-center gap-2.5 py-3 opacity-85 ${
                  rest > 0 ? 'border-b border-[#f0f0f0]' : ''
                }`}
              >
                <span className="text-base leading-none">⏳</span>
                <span className="min-w-0 flex-1 text-[.8125rem] text-[#5d5d5d]">
                  {fmtInt(thin.length)} {thin.length === 1 ? 'campaña' : 'campañas'} con pocas visitas
                  todavía.
                </span>
                <button
                  type="button"
                  onClick={() => setExpandedThin(true)}
                  className="shrink-0 text-xs text-[#0059d5]"
                >
                  Ver
                </button>
              </div>
            ))}

          {rest > 0 && (
            <div className="py-2.5 text-center">
              <button
                type="button"
                onClick={() => setExpandedRest(true)}
                className="text-[.8125rem] text-[#0059d5]"
              >
                Ver {rest === 1 ? 'la campaña restante' : `las ${fmtInt(rest)} campañas restantes`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Quiet, always present, never behind a disclosure — the caveats are part of the numbers. */
function HonestyBlock({ data }: { data: TrafficValueResponse }) {
  return (
    <div className="mt-1 flex flex-col gap-2 px-0.5">
      <span className="text-xs leading-relaxed text-[#888]">
        Medimos lo que pasa en tu tienda después del clic, y le damos el crédito a la última fuente
        que trajo cada visita. Una campaña que te hace conocer, y no cierra la venta ese mismo día,
        va a aparecer más floja de lo que realmente es.
      </span>
      {data.unattributed_conversion_pct != null && (
        <span className="text-xs tabular-nums text-[#888]">
          No pudimos identificar de dónde vino el {Math.round(data.unattributed_conversion_pct)}% de
          tus ventas.
        </span>
      )}
      {data.origin_coverage_pct != null && (
        <span className="text-xs tabular-nums text-[#888]">
          Alcanzamos a identificar el origen del {Math.round(data.origin_coverage_pct)}% de tus
          visitas.
        </span>
      )}
    </div>
  )
}

function StateCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-[#e7e7e7] bg-white p-4">
      <span className="text-sm leading-relaxed text-[#5d5d5d]">{children}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export function MerchantTraffic() {
  const { user } = useAuth()
  const storeName = user?.store_name || 'Tu tienda'
  const [data, setData] = useState<TrafficValueResponse | null>(null)
  const [tags, setTags] = useState<CampaignTags>({})
  const [state, setState] = useState<'loading' | 'error' | 'no-store' | 'ready'>('loading')
  const [sharing, setSharing] = useState<TrafficCampaignRow | null>(null)

  useEffect(() => {
    const storeId = authService.getCurrentStoreId()
    if (!storeId) {
      setState('no-store')
      return
    }
    trackAppEvent('traffic_viewed')
    let alive = true
    trafficService
      .getTrafficValue(storeId, WINDOW_DAYS)
      .then((res) => {
        if (!alive) return
        setData(res)
        setState('ready')
      })
      .catch(() => alive && setState('error'))
    // Tags are optional decoration: a failure here must not take the screen down.
    trafficService
      .getCampaignTags(storeId)
      .then((t) => alive && setTags(t))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])

  const answer = (row: TrafficCampaignRow, objective: Objective) => {
    const storeId = authService.getCurrentStoreId()
    if (!storeId) return
    const key = tagKey(row.channel, row.campaign)
    // Optimistic: the ask is a two-tap aside, so it must feel instant or it won't get answered.
    setTags((prev) => ({ ...prev, [key]: { objective } }))
    trackAppEvent('campaign_tagged', { objective })
    trafficService
      .setCampaignTags(storeId, [{ channel: row.channel, campaign: row.campaign, objective }])
      .catch(() => {
        setTags((prev) => {
          const next = { ...prev }
          delete next[key]
          return next
        })
      })
  }

  const share = (row: TrafficCampaignRow) => {
    trackAppEvent('traffic_shared', { quadrant: row.quadrant })
    setSharing(row)
  }

  const channels = useMemo(() => data?.channels ?? [], [data])

  // The map plots every measured campaign in the store. It derives its own crossing point
  // (the store's session-weighted average) from these rows, so position always means
  // "vs the rest of your traffic".
  const plottedRows = useMemo(() => mapRows(channels), [channels])
  const showMap = isMapMeaningful(plottedRows)

  const findings = (data?.findings ?? [])
    .map((f) => ({ finding: f, copy: findingCopy(f) }))
    .filter((x): x is { finding: TrafficFinding; copy: FindingCopy } => !!x.copy)
    .slice(0, 2)

  // A campaign asked about in a finding must not be asked about again in its row.
  const nudgedElsewhere = new Set(
    findings
      .filter((f) => f.copy.nudge && f.finding.row)
      .map((f) => tagKey(f.finding.row!.channel, f.finding.row!.campaign))
  )

  const header = (
    <div className="flex flex-col gap-1.5 px-0.5">
      <Link to="/merchant" className="self-start text-[.8125rem] text-[#0059d5] no-underline">
        ← Volver
      </Link>
      <div className="text-xl font-bold text-[#0a0a0a] md:text-2xl">Tu tráfico</div>
      <span className="text-xs text-[#5d5d5d] md:text-[.8125rem]">
        De dónde vienen tus visitas y cuáles te sirven · {PERIOD_LABEL}
      </span>
    </div>
  )

  if (state === 'loading') {
    return (
      <div className="flex flex-col gap-5">
        {header}
        <div className="py-6 text-center text-sm text-[#5d5d5d]">Cargando tu tráfico…</div>
      </div>
    )
  }

  if (state === 'no-store') {
    return (
      <div className="flex flex-col gap-5">
        {header}
        <StateCard>No pudimos identificar tu tienda. Volvé a iniciar sesión.</StateCard>
      </div>
    )
  }

  if (state === 'error' || !data) {
    return (
      <div className="flex flex-col gap-5">
        {header}
        <StateCard>No pudimos cargar tu tráfico. Probá de nuevo en un rato.</StateCard>
      </div>
    )
  }

  if (!channels.length) {
    return (
      <div className="flex flex-col gap-5">
        {header}
        <StateCard>
          Todavía no tenemos suficientes visitas por campaña para decirte cuál te sirve. Volvé a
          mirar en unos días.
        </StateCard>
        <HonestyBlock data={data} />
      </div>
    )
  }

  const list = (
    <div className="flex flex-col gap-5">
      {channels.map((group) => (
        <ChannelSection
          key={group.channel}
          group={group}
          tags={tags}
          nudgedElsewhere={nudgedElsewhere}
          onAnswer={answer}
        />
      ))}
    </div>
  )

  return (
    <div className="flex flex-col gap-5">
      {header}

      {/*
        TWO grid items, not five placed ones. The narrow column is a single stacked item
        (avisos → mapa), the wide one is the channel list.

        Explicit row placement was wrong here: the list spanned both rows, and a grid
        distributes a spanning item's height across the rows it covers — so the very tall
        list inflated row 1 and stranded the notices ~1000px below the chart. Keeping the
        left column as ONE item removes the row sizing from the equation entirely, and the
        DOM order still reads avisos → mapa → lista for the single-column mobile layout.
      */}
      <div
        className={
          showMap
            ? 'flex flex-col gap-5 md:grid md:grid-cols-[460px_1fr] md:items-start md:gap-6'
            : 'flex flex-col gap-5'
        }
      >
        <div className="flex flex-col gap-4">
          {/* Half-ramped window announces itself rather than reading as a real dip. */}
          {!data.window_complete && (
            <div className="rounded-lg border border-[#e7e7e7] bg-[#fafafa] px-4 py-3">
              <span className="text-[.8125rem] leading-relaxed text-[#5d5d5d]">
                Estos números todavía no cubren toda la semana — se van a completar en los
                próximos días.
              </span>
            </div>
          )}
          {findings.map(({ finding, copy }, i) => (
            <FindingCard
              key={`${finding.kind}-${finding.channel}-${i}`}
              finding={finding}
              copy={copy}
              tags={tags}
              onAnswer={answer}
              onShare={share}
            />
          ))}

          {showMap && (
            <div className="rounded-lg border border-[#e7e7e7] bg-white px-4 pb-2.5 pt-3.5 md:hidden">
              <CampaignMap rows={plottedRows} />
            </div>
          )}
          {showMap && (
            <div className="hidden flex-col gap-2 rounded-lg border border-[#e7e7e7] bg-white px-4 pb-3 pt-4 md:flex">
              <Text fontWeight="medium">Tus campañas de un vistazo</Text>
              <CampaignMap rows={plottedRows} showAllNames width={428} height={320} />
            </div>
          )}
        </div>

        {list}
      </div>

      <HonestyBlock data={data} />

      {sharing && (
        <ShareFindingSheet
          row={sharing}
          storeName={storeName}
          periodLabel={PERIOD_LABEL}
          onClose={() => setSharing(null)}
        />
      )}
    </div>
  )
}
